import React, { useRef, useEffect, useState } from "react";
import Hls from "hls.js";
import { Play } from "lucide-react";

type VideoShowPlayerProps = {
  src: string;
  onClose: () => void;
  autoPlay: boolean;
};


const VideoShowPlayer: React.FC<VideoShowPlayerProps> = ({ src, onClose, autoPlay }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [, setHls] = useState<Hls | null>(null)
  const [showThumbnail, setShowThumbnail] = useState(true)



  useEffect(() => {
    const authToken = localStorage.getItem("AUTH_TOKEN")
    setToken(authToken)
  }, [])

  useEffect(() => {
    if (!videoRef.current || !token) return

    let newHls: Hls | null = null

    if (Hls.isSupported()) {
      newHls = new Hls({
        xhrSetup: (xhr, url) => {
          xhr.setRequestHeader("Authorization", `Bearer ${token}`)
        },
      })

      newHls.loadSource(src)
      newHls.attachMedia(videoRef.current)

      newHls.on(Hls.Events.MANIFEST_PARSED, () => {
        setLoading(false)
        setShowThumbnail(false) 
        videoRef.current?.play().catch(e => console.log("Autoplay prevented:", e))
      })

      newHls.on(Hls.Events.ERROR, (event, data) => {
        console.error("HLS error:", data)
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              newHls?.startLoad()
              break
            case Hls.ErrorTypes.MEDIA_ERROR:
              newHls?.recoverMediaError()
              break
            default:
              newHls?.destroy()
              break
          }
        }
      })

      setHls(newHls)
    } else if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
  
      videoRef.current.src = src
      videoRef.current.addEventListener("loadedmetadata", () => {
        setLoading(false)
        setShowThumbnail(false)
        videoRef.current?.play().catch(e => console.log("Autoplay prevented:", e))
      })
    }

    return () => {
      if (newHls) {
        newHls.destroy()
      }
    }
  }, [src, token])

  const handlePlay = () => {
    setShowThumbnail(false)
    videoRef.current?.play()
  }

  if (!token) {
    return <div className="flex items-center justify-center h-64 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl">Loading token...</div>
  }

  return (
    <div className="relative aspect-[16/9] rounded-2xl overflow-hidden shadow-xl">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 z-10">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      )}
      
      {showThumbnail && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-cover bg-center z-10 cursor-pointer"
          style={{ backgroundImage: `url(${src.split('?')[0]})` }}
          onClick={handlePlay}
        >
          <div className="bg-black bg-opacity-50 rounded-full p-5 backdrop-blur-sm shadow-2xl">
            <Play className="text-white" size={36} />
          </div>
        </div>
      )}
      
      <video
        ref={videoRef}
        controls
        className="w-full h-full"
        style={{ display: showThumbnail ? 'none' : 'block' }}
      />
    </div>
  )
}


export default VideoShowPlayer;